import { useCallback, useState } from 'react'
import { CreditLimitRequest } from '../types'

/**
 * TEMPORARY MOCK — use this in place of useCreditLimitRequest while testing
 * the UI. Skips the real onUpdateCreditLimit(customerId, newLimit) call and
 * just resolves locally after a short fake delay, so you can confirm the
 * quick-add buttons, validation, and pending/approved pill all work before
 * your real update function or auth/user context is wired up.
 *
 * Swap back to the real ./useCreditLimitRequest.ts once confirmed.
 */

interface UseCreditLimitRequestArgs {
    customerId: string
    initialLimit: number
    requestedByName?: string
    requestedByRole?: string
    onUpdateCreditLimit?: (customerId: string, newLimit: number) => Promise<void>
}

const FAKE_DELAY_MS = 500
// Force a failure the next time submit() runs, to test the rejected/error state. Flip in devtools if needed.
const FORCE_NEXT_FAILURE = false

export function useCreditLimitRequest({
    customerId,
    initialLimit,
    requestedByName = 'Test Admin',
    requestedByRole = 'Admin',
}: UseCreditLimitRequestArgs) {
    const [creditLimit, setCreditLimit] = useState<number>(initialLimit)
    const [draftLimit, setDraftLimit] = useState<string>(String(initialLimit))
    const [request, setRequest] = useState<CreditLimitRequest | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [validationError, setValidationError] = useState<string | null>(null)

    const handleDraftChange = useCallback(
        (value: string) => {
            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                setDraftLimit(value)
                if (validationError) setValidationError(null)
            }
        },
        [validationError],
    )

    const bump = useCallback(
        (amount: number) => {
            const base = parseFloat(draftLimit) || creditLimit
            const next = Math.round((base + amount) * 100) / 100
            setDraftLimit(String(next))
            if (validationError) setValidationError(null)
        },
        [draftLimit, creditLimit, validationError],
    )

    const resetDraft = useCallback(() => {
        setDraftLimit(String(creditLimit))
        setValidationError(null)
    }, [creditLimit])

    const submit = useCallback(async () => {
        const parsed = parseFloat(draftLimit)
        if (isNaN(parsed) || parsed <= 0) {
            setValidationError('Enter a valid positive number for the credit limit.')
            return
        }
        if (parsed === creditLimit) {
            setValidationError('Enter a limit different from the current one.')
            return
        }

        const pendingRequest: CreditLimitRequest = {
            requestedBy: requestedByName,
            requestedByRole,
            requestedAt: new Date(),
            previousLimit: creditLimit,
            newLimit: parsed,
            status: 'pending',
        }
        setRequest(pendingRequest)
        setIsSubmitting(true)

        await new Promise((resolve) => setTimeout(resolve, FAKE_DELAY_MS))

        if (FORCE_NEXT_FAILURE) {
            setRequest({
                ...pendingRequest,
                status: 'rejected',
                resolvedAt: new Date(),
                errorMessage: 'Mock failure — flip FORCE_NEXT_FAILURE off to succeed.',
            })
        } else {
            setCreditLimit(parsed)
            setRequest({ ...pendingRequest, status: 'approved', resolvedAt: new Date() })
        }
        setIsSubmitting(false)
    }, [draftLimit, creditLimit, requestedByName, requestedByRole])

    return {
        creditLimit,
        draftLimit,
        request,
        isSubmitting,
        validationError,
        handleDraftChange,
        bump,
        resetDraft,
        submit,
    }
}
